import React from "react";

/**
 * Global React Error Boundary.
 * Renders a friendly inline panel when a child component crashes,
 * instead of letting React unmount the whole subtree (blank page).
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error("[ErrorBoundary]", error, info?.componentStack);
    }

    handleReset = () => {
        this.setState({ error: null });
    };

    handleReload = () => {
        window.location.href = "/";
    };

    render() {
        if (this.state.error) {
            const msg = this.state.error.message || String(this.state.error);
            return (
                <div data-testid="error-boundary" className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4 py-12">
                    <div className="w-full max-w-lg bg-white border border-[#990000]/20 rounded-xl p-8 text-center">
                        <div className="w-14 h-14 rounded-full bg-[#990000] flex items-center justify-center mx-auto mb-5">
                            <span className="text-[#FFCC00] font-heading font-extrabold text-2xl">!</span>
                        </div>
                        <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#990000] mb-2">Something broke</div>
                        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] tracking-tight mb-3">
                            This page hit a snag
                        </h1>
                        <p className="text-stone-600 text-sm mb-6 leading-relaxed">
                            A small bug crashed this view, but the rest of TrojanTrails is fine. Try going back home, or share what you were doing via the feedback form.
                        </p>
                        <pre
                            data-testid="error-boundary-message"
                            className="text-left text-xs font-mono text-[#5C0000] bg-[#990000]/5 border border-[#990000]/15 rounded-lg p-3 mb-6 overflow-x-auto whitespace-pre-wrap break-words"
                        >{msg}</pre>
                        <div className="flex justify-center gap-3 flex-wrap">
                            <button
                                data-testid="error-boundary-retry-btn"
                                onClick={this.handleReset}
                                className="h-10 px-5 rounded-lg border border-stone-300 text-stone-700 hover:border-[#990000] hover:text-[#990000] text-sm font-medium"
                            >
                                Try again
                            </button>
                            <button
                                data-testid="error-boundary-home-btn"
                                onClick={this.handleReload}
                                className="h-10 px-5 rounded-lg bg-[#990000] hover:bg-[#7A0000] text-white text-sm font-medium"
                            >
                                Back to home
                            </button>
                        </div>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}
